/**
 * What makes the protocol PDF clickable: links from an issue's thumbnails to
 * its files' pages in the appendix and back to the issue, and each original
 * file carried inside the PDF behind a paperclip.
 *
 * Both directions are drawn before their other end exists — the table comes
 * before the appendix, and the appendix links back to rows drawn earlier — so
 * spots and targets are collected while drawing and joined once at the end.
 */
import {
  FileEmbedder,
  PDFArray,
  PDFDict,
  PDFHexString,
  PDFName,
  type PDFDocument,
  type PDFPage,
  type PDFRef,
} from "pdf-lib";
import type { ProtocolOriginal } from "@/domains/pm/utils/protocolAttachments";

/** A clickable area on a page, in PDF points. */
export interface LinkSpot {
  page: PDFPage;
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Where a link lands: a page, scrolled so `top` is at the top of the view. */
export interface LinkTarget {
  page: PDFPage;
  top: number;
}

export interface ProtocolLinks {
  /** Each issue's row in the table — where an attachment's way back lands. */
  issueRows: Map<number, LinkTarget>;
  /** Each attachment's first appearance in the appendix. */
  attachmentPages: Map<number, LinkTarget>;
  toAttachment: Array<{ spot: LinkSpot; number: number }>;
  toIssue: Array<{ spot: LinkSpot; issueId: number }>;
  /** Where each file's paperclip goes: beside its caption, wherever it is. */
  paperclips: Array<{ spot: LinkSpot; number: number }>;
}

export function createProtocolLinks(): ProtocolLinks {
  return {
    issueRows: new Map(),
    attachmentPages: new Map(),
    toAttachment: [],
    toIssue: [],
    paperclips: [],
  };
}

/** The paperclip's box: the icon a viewer draws for a file attachment. */
export const PAPERCLIP_SIZE = 10;

function addAnnotation(doc: PDFDocument, page: PDFPage, annotation: PDFDict): void {
  page.node.addAnnot(doc.context.register(annotation));
}

function rect({ x, y, width, height }: LinkSpot): number[] {
  return [x, y, x + width, y + height];
}

function addGoTo(doc: PDFDocument, spot: LinkSpot, target: LinkTarget): void {
  addAnnotation(
    doc,
    spot.page,
    doc.context.obj({
      Type: "Annot",
      Subtype: "Link",
      Rect: rect(spot),
      // No border: the thumbnail or heading is the affordance.
      Border: [0, 0, 0],
      // A GoTo action, not a bare /Dest: Chrome's viewer ignores a /Dest whose
      // left and zoom are null (keep the reader's own), yet follows the very
      // same destination given as an action. Checked by clicking both there.
      A: { Type: "Action", S: "GoTo", D: [target.page.ref, "XYZ", null, target.top, null] },
    })
  );
}

/**
 * Embeds the file and lists it in the document's attachments, the panel every
 * viewer that supports them opens from its paperclip. Keyed by number, so two
 * files that share a name are both kept, and in order, as the name tree
 * requires.
 */
async function embedOriginal(
  doc: PDFDocument,
  number: number,
  filename: string,
  original: ProtocolOriginal
): Promise<PDFRef> {
  const ref = await FileEmbedder.for(original.bytes, filename, {
    mimeType: original.mimeType ?? undefined,
  }).embedIntoContext(doc.context);
  const names = doc.catalog.lookupMaybe(PDFName.of("Names"), PDFDict) ?? doc.context.obj({});
  doc.catalog.set(PDFName.of("Names"), names);
  const files = names.lookupMaybe(PDFName.of("EmbeddedFiles"), PDFDict) ?? doc.context.obj({});
  names.set(PDFName.of("EmbeddedFiles"), files);
  const list = files.lookupMaybe(PDFName.of("Names"), PDFArray) ?? doc.context.obj([]);
  files.set(PDFName.of("Names"), list);
  list.push(PDFHexString.fromText(`${String(number).padStart(4, "0")} ${filename}`));
  list.push(ref);
  return ref;
}

/**
 * Joins what was collected: every link whose other end exists, and every
 * original embedded once with a paperclip at each of its captions.
 */
export async function finishProtocolLinks(
  doc: PDFDocument,
  links: ProtocolLinks,
  originals: ReadonlyMap<number, { filename: string; original: ProtocolOriginal }>
): Promise<void> {
  for (const { spot, number } of links.toAttachment) {
    const target = links.attachmentPages.get(number);
    if (target) addGoTo(doc, spot, target);
  }
  for (const { spot, issueId } of links.toIssue) {
    const target = links.issueRows.get(issueId);
    if (target) addGoTo(doc, spot, target);
  }
  const embedded = new Map<number, PDFRef>();
  for (const [number, { filename, original }] of [...originals].sort(([a], [b]) => a - b)) {
    embedded.set(number, await embedOriginal(doc, number, filename, original));
  }
  for (const { spot, number } of links.paperclips) {
    const file = embedded.get(number);
    const filename = originals.get(number)?.filename;
    if (!file || !filename) continue;
    addAnnotation(
      doc,
      spot.page,
      doc.context.obj({
        Type: "Annot",
        Subtype: "FileAttachment",
        Rect: rect(spot),
        FS: file,
        Name: "Paperclip",
        Contents: PDFHexString.fromText(filename),
      })
    );
  }
}
